import apolloClient from "@/graphql/client";
import {
  type ConsoleMessage,
  ConsoleMessagesDocument,
  CreateMysqlDbDocument,
  CreatePermissionDocument,
  CreateRestartTaskDocument,
  CtxDocument,
  type CtxError,
  type CtxErrorDetail,
  type CustomerMysqlDb,
  DeletePermissionDocument,
  DeleteRestartTaskDocument,
  DeleteSaveGamesDocument,
  FailureScope,
  ForceUpdateDocument,
  GameServerQueryDocument,
  type Maybe,
  Region,
  ReinstallServiceDocument,
  ResetFtpPasswordDocument,
  ResetMysqlDbPasswordDocument,
  RestartServiceDocument,
  type RestartTaskInput,
  type RootNamespace,
  type Rsid,
  type Rsid_Input,
  RunCommandDocument,
  ServiceCtxDocument,
  ServiceEventsDocument,
  type ServiceSensors,
  ServiceSensorsDocument,
  type ServiceState,
  ServiceStateDocument,
  Service_States,
  SetAutoUpdateDocument,
  SetCustomHostnameDocument,
  SetWaitUntilEmptyDocument,
  SidDocument,
  SkipBroadcastDocument,
  StopServiceDocument,
  type WaitUntilEmptyFieldsFragment,
} from "@/graphql/index";
import {
  FingerprintedError,
  type FingerprintedErrorOptions,
  errorMessage,
  inheritFingerprint,
} from "@/util/fingerprintedError";
import { useApolloErrorHandling } from "@/util/useApolloErrorHandling";
import type { ApolloError } from "@apollo/client/core";
import { useGpToasts } from "@gportal/uikit";
import * as Sentry from "@sentry/vue";
import structuredClone from "@ungap/structured-clone";
import { useDocumentVisibility, useWindowFocus } from "@vueuse/core";
import { GraphQLError } from "graphql/error/GraphQLError";
import { Source } from "graphql/language";
import { defineStore } from "pinia";
import { computed, ref, toRaw, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import type { Subscription } from "zen-observable-ts";
import buildProfile, { defaultProfile } from "../game_profiles";
import type { GameProfile } from "../game_profiles/types";
import { type CtxLoadTrigger, backupEventTrigger, ctxLoadUrlParams } from "../util/ctxLoadTrigger";
import { createCtxReloadScheduler } from "../util/ctxReloadScheduler";
import { isValidInt32 } from "../util/misc";
import { useClientStore } from "./client";
import { useMaintenanceStore } from "./maintenance";

type ServiceEntry = RootNamespace & {
  gameProfile: GameProfile | undefined;
  sensors?: ServiceSensors;
  consoleMessages: ConsoleMessage[];
  changeTracking?: {
    publicConfigs: Record<string, Record<string, unknown>>;
    changedFields: Record<string, Array<string>>;
  };
};

// Raised whenever the service context (cfgContext / sid) fails to load.
// Extends FingerprintedError (util/fingerprintedError.ts) purely for stable
// Sentry grouping — see that file for why: messages here can embed upstream
// GraphQL/gRPC error text with variable data (TV-1870).
export class CtxLoadError extends FingerprintedError {
  name = "CtxLoadError";
  // The underlying GraphQL errors that caused this load failure, if any.
  errors: ReadonlyArray<GraphQLError> = [];
  // Sentry event ids captured for this error, populated by handleCtxLoadError
  // below (one per entry in `errors`, or one for the error itself).
  errorIds: Array<string> = [];

  constructor(context: string, options?: FingerprintedErrorOptions, errors?: ReadonlyArray<GraphQLError>) {
    super(context, options);
    this.errors = errors ?? [];
  }
}

// A CtxLoadError subtype specifically for "the service/gameserver id doesn't
// exist". Kept as a distinct class (rather than a fingerprint value) because
// callers use `instanceof ServiceNotFoundError` to skip the retry/backoff
// logic in handleCtxLoadError — a not-found service will never succeed on retry.
export class ServiceNotFoundError extends CtxLoadError {
  name = "ServiceNotFoundError";
}

// Raised when any of the service's GraphQL subscriptions (state, sensors,
// console, events, gameserver query) fail to establish or error out mid-stream.
// Same FingerprintedError rationale as CtxLoadError: subscription error
// messages routinely wrap raw gRPC/Apollo error text (TV-1870's original
// trigger was exactly this class, via the console-messages subscription).
export class SubscriptionError extends FingerprintedError {
  name = "SubscriptionError";
}

type CtxErrorDetailParsed = CtxErrorDetail & {
  args: unknown[];
};

type CtxErrorParsed = CtxError & {
  error: CtxErrorDetailParsed;
};

function buildServiceEntry(): ServiceEntry {
  return {
    gameProfile: defaultProfile(),
    consoleMessages: [],
  };
}

/** The server a ctx load is for. A load by gameserver ID only learns its `sid` from the sid query. */
type LoadTarget = { region: Region | undefined; sid?: number; gameserverId?: number };

function sameLoadTarget(a: LoadTarget | undefined, b: LoadTarget | undefined) {
  if (!a || !b || a.region !== b.region) return false;
  if (a.sid && b.sid) return a.sid === b.sid;
  return !!a.gameserverId && a.gameserverId === b.gameserverId;
}

export const useServiceStore = defineStore("service", () => {
  const { createToast } = useGpToasts();
  const { handleApolloErrorDefault } = useApolloErrorHandling();
  const { t } = useI18n();
  const client = useClientStore();
  const maintenance = useMaintenanceStore();
  const isLoading = ref<boolean>(false);
  const ctx = ref(buildServiceEntry());
  const ctxLoadError = ref<CtxLoadError>();
  const ctxErrors = ref<CtxErrorParsed[]>();
  const ctxRetry = ref<{ nextAt: number; count: number }>({ nextAt: 0, count: 0 });
  let ctxRetryTimeout: NodeJS.Timeout | undefined = undefined;
  const lostFocus = ref<boolean>(false);
  const focused = useWindowFocus();
  const visibility = useDocumentVisibility();
  /** The ctx load running right now. A second load of the same server joins it instead of being dropped */
  let inFlightLoad: { rsid: Rsid_Input; done: Promise<void> } | undefined;
  /**
   * The server the panel is on, set by every load as it starts: during a server switch it already names the
   * new server, and it still does when that load failed. Loads and reloads of any other server are stale.
   */
  let loadTarget: LoadTarget | undefined;
  /** Counts the ctx loads applied to `ctx`, so a watcher can tell a load's change from a subscription's */
  const ctxLoadCount = ref(0);
  const gameServerState = ref();
  const sensors = ref();
  const commitInProgress = ref<boolean>(false);
  const expertModeActive = ref<boolean>(false);
  const showWarning = ref<boolean>(true);
  const currentRsid = computed(() => ctx.value.service?.config?.rsid);
  /** Wait-until-empty choices and limits (TR-5485); null where the profile doesn't offer the setting. */
  const waitUntilEmpty = computed(
    () =>
      (ctx.value.profile as { waitUntilEmpty?: WaitUntilEmptyFieldsFragment | null } | undefined)?.waitUntilEmpty ??
      null,
  );
  const serviceStates = computed(() => ctx.value?.service?.currentState?.state || Service_States.Unknown);
  const serviceIsTransitioning = computed(
    () =>
      ctx.value?.service?.currentState?.fsmIsTransitioning || ctx.value?.service?.currentState?.fsmIsExclusiveLocked,
  );
  const interactivityLocked = computed(
    () =>
      isLoading.value ||
      serviceIsTransitioning.value ||
      commitInProgress.value ||
      maintenance.maintenanceInteractivityLocked ||
      !!ctxLoadError.value,
  );
  // Split and filter console messages per game profile.
  // Processed incrementally so that stateful game-profile filters (e.g. line-joining)
  // work correctly across message boundaries. The raw message is NOT pre-trimmed so that
  // trailing ANSI codes (e.g. color resets) reach the filter instead of being dropped.
  const filteredConsoleMessages = ref<ConsoleMessage[]>([]);
  let _lastProcessedCount = 0;

  let _flushTimer: ReturnType<typeof setTimeout> | null = null;

  function _flushConsoleFilter() {
    _flushTimer = null;
    const filter = ctx.value.gameProfile?.consoleFilter;
    const flushed = filter?.flush?.();
    if (flushed) {
      const msgs = ctx.value.consoleMessages;
      const last = msgs[msgs.length - 1];
      // Use null channel so the flushed line inherits default styling, not the channel
      // of whatever raw event happened to be last (which could be green "event" channel).
      if (last) filteredConsoleMessages.value.push({ ...last, message: flushed, channel: null });
    }
  }

  function _processNewConsoleMessages() {
    const messages = ctx.value.consoleMessages;
    const filter = ctx.value.gameProfile?.consoleFilter;
    const preprocess = ctx.value.gameProfile?.consoleMessagePreprocessor;
    for (let i = _lastProcessedCount; i < messages.length; i++) {
      const message = messages[i];
      // Event-channel messages (buffer_playback_finished, ping events, etc.) bypass the
      // game filter entirely — they're control/system messages that should always show.
      // Flush any buffered pending content first so ordering is preserved.
      if (filter && message.channel === "event") {
        const flushed = filter.flush?.();
        if (flushed) filteredConsoleMessages.value.push({ ...message, message: flushed, channel: null });
        const trimmed = message.message.trim();
        if (trimmed) filteredConsoleMessages.value.push({ ...message, message: trimmed });
        continue;
      }
      const rawMessage = preprocess ? preprocess(message.message) : message.message;
      for (const rawLine of rawMessage.split("\n")) {
        if (filter) {
          const filtered = filter(rawLine);
          if (filtered === null) continue;
          filteredConsoleMessages.value.push({ ...message, message: filtered });
        } else {
          const trimmed = rawLine.trim();
          if (trimmed) filteredConsoleMessages.value.push({ ...message, message: trimmed });
        }
      }
    }
    _lastProcessedCount = messages.length;
    // Flush any buffered pending content after a short quiet period.
    // This ensures stateful filters (e.g. BungeeCord line-joining) don't hold
    // the last entry indefinitely when no further messages arrive.
    if (filter?.flush) {
      if (_flushTimer) clearTimeout(_flushTimer);
      _flushTimer = setTimeout(_flushConsoleFilter, 100);
    }
  }

  function _resetFilteredConsoleMessages() {
    if (_flushTimer) {
      clearTimeout(_flushTimer);
      _flushTimer = null;
    }
    filteredConsoleMessages.value = [];
    _lastProcessedCount = 0;
    ctx.value.gameProfile?.consoleFilter?.reset?.();
    ctx.value.gameProfile?.consoleFilterReset?.();
    // Re-process all existing messages immediately so buffer playback is correctly
    // filtered/preprocessed now that the game profile is available.
    _processNewConsoleMessages();
  }

  // Reset when navigating to a different server (ctx reference changes).
  watch(ctx, _resetFilteredConsoleMessages);

  // Also reset when the game profile is set/changed — buffer playback messages typically arrive
  // before the profile is resolved, so they'd be processed without the profile's preprocessor
  // and filter. Re-process from scratch once the profile is available.
  watch(() => ctx.value.gameProfile, _resetFilteredConsoleMessages);

  // Process new messages as they arrive (watch the length, not deep contents).
  watch(() => ctx.value.consoleMessages.length, _processNewConsoleMessages);

  const consoleMessages = computed(() => filteredConsoleMessages.value);

  const subscriptions: {
    state?: Subscription;
    sensors?: Subscription;
    console?: Subscription;
    gameServer?: Subscription;
    events?: Subscription;
  } = {
    state: undefined,
    sensors: undefined,
    console: undefined,
    gameServer: undefined,
    events: undefined,
  };

  // Cap for the client-side console buffer. The console view splits messages into
  // lines, so the rendered line count can exceed this, but it stays bounded.
  const CONSOLE_MESSAGE_LIMIT = 2000;
  // Errors that mean the console is permanently unavailable for this service;
  // re-subscribing would only produce more doomed attempts.
  const CONSOLE_PERMANENT_ERRORS = ["Service does not support console"];
  const CONSOLE_RETRY_BASE_DELAY = 2000;
  const CONSOLE_RETRY_MAX_DELAY = 60000;
  // Backoff schedule for ctx-load retries: first two attempts use a short
  // random delay, later attempts back off exponentially up to a cap, with a
  // fixed head start added on top so the jump from "short" to "long" delays
  // isn't abrupt.
  const CTX_RETRY_SHORT_DELAY_MIN = 10000;
  const CTX_RETRY_SHORT_DELAY_MAX = 30000;
  const CTX_RETRY_LONG_DELAY_HEAD_START = 30000;
  const CTX_RETRY_BASE_DELAY = 100;
  const CTX_RETRY_MAX_DELAY = 1200000;
  const consoleRetry: { count: number; timeout?: NodeJS.Timeout; rsid?: Rsid_Input } = {
    count: 0,
    timeout: undefined,
    rsid: undefined,
  };

  function serviceCtx({ ctxData, ctxErrorsData }: { ctxData: ServiceEntry; ctxErrorsData: CtxError[] }) {
    const previousRsid = ctx.value.service?.config?.rsid;
    const loadedRsid = ctxData.service?.config?.rsid;
    if (
      ctx.value.service?.currentState &&
      ctx.value.service.currentState.state !== Service_States.Unknown &&
      ctxData.service &&
      // Only the same server's state: after a server switch the previous one's would show until the new
      // subscription's first message, and its end would look like a finished transition (TR-5544)
      previousRsid?.id === loadedRsid?.id &&
      previousRsid?.region === loadedRsid?.region
    ) {
      // preserve local state from subscription if we seem to know more than nothing
      ctxData.service.currentState = ctx.value.service.currentState;
    }
    ctx.value = {
      ...ctx.value,
      ...ctxData,
    };
    ctxLoadCount.value += 1;
    ctxErrors.value = ctxErrorsData.map((err) => {
      err.error.args = err.error.args.map((arg) => arg && JSON.parse(arg));
      return err;
    });
  }

  function setWarningMsg(show: boolean) {
    showWarning.value = show;
  }

  function serviceState(state: ServiceState) {
    if (ctx.value.service) {
      ctx.value.service.currentState = state;
      if (ctx.value.service.config) ctx.value.service.config.state = state.state;
    }
  }

  function serviceTargetState(runningState: Service_States) {
    if (ctx.value.service?.config) ctx.value.service.config.state = runningState;
  }

  function setConfField({ config, field, value }) {
    if (!ctx.value?.profile) return;
    const pc = ctx.value.profile.publicConfigs;
    if (pc[config] == null) {
      pc[config] = {};
    }
    pc[config][field] = value;
  }

  function setConfig({ config, value }) {
    if (ctx.value.profile) ctx.value.profile.publicConfigs[config] = value;
  }

  function discardConfChanges() {
    if (ctx.value.profile && ctx.value.changeTracking) {
      // Restore original publicConfigs object
      ctx.value.profile.publicConfigs = structuredClone(toRaw(ctx.value.changeTracking.publicConfigs));
      // Reset "dirty" flag immediately (the watcher will be debounced on this)
      ctx.value.changeTracking.changedFields = {};
    }
  }

  type LoadAllOptions = {
    region: Region | undefined;
    sid?: number;
    gameserverId?: number;
    /** Why this load happens, sent along with the ctx request (TR-5544) */
    trigger: CtxLoadTrigger;
    /** Further reload requests this load also answers (coalesced or deferred) */
    merged?: CtxLoadTrigger[];
  };

  // Every load is tracked by the scheduler, so a reload waits for all of them. A route load does not wait
  // for a reload of the previous server: that one's result is discarded as stale instead.
  function loadAll(options: LoadAllOptions) {
    return reloadScheduler.track(runLoadAll(options));
  }

  async function runLoadAll({ region, sid: sidParam, gameserverId, trigger, merged = [] }: LoadAllOptions) {
    let sid = sidParam;
    isLoading.value = true;
    if (!client.isAuthenticated) {
      console.error("loadAll cancelled since we're not authenticated right now.");
      return;
    }
    // This load decides which server the panel is on, and replaces a retry that is still waiting
    const target: LoadTarget = { region, sid, gameserverId };
    loadTarget = target;
    clearCtxRetry();
    console.debug("Loading server");
    // Fetch SID if it does not exist yet
    if (!sid && gameserverId) {
      // Validate gameserver ID as valid i32 to prevent GraphQL from throwing this as an error
      if (!isValidInt32(gameserverId)) {
        await handleCtxLoadError({
          error: new ServiceNotFoundError("Service not found"),
          region,
          sid,
          gameserverId,
          trigger,
        });
        return;
      }
      console.debug("Querying for service ID based on gameserver ID:", gameserverId);
      let result;
      try {
        result = await apolloClient.query({
          query: SidDocument,
          variables: {
            gameserverId,
            region,
          },
        });
      } catch (e) {
        const err = e as ApolloError;
        // The rewrite normalises a variable transport message down to a constant
        // one. It still matters: `handleCtxLoadError` renders it as the event's
        // `detail`, and util/error.ts classifies the UI error by looking for
        // "Failed to fetch" in it, which the replacement keeps.
        const failedToFetch = err.message.includes("Failed to fetch");
        if (failedToFetch) {
          err.message = "Unable to load context: Failed to fetch sid";
        }
        await handleCtxLoadError({
          // `e` may be an ApolloError whose message embeds a transport-level repr
          // (e.g. a fetch failure detail) that can vary run to run — explicit
          // fingerprints keep this throw site stable in Sentry. A failed fetch
          // gets its own key: it is the common, benign case and had a clean
          // group of its own under the old message-based grouping, so folding it
          // in with genuine Apollo client errors would lose signal rather than
          // gain it.
          error: new CtxLoadError(
            "GraphQL client error",
            { fingerprint: failedToFetch ? "sid-query-fetch-failed" : "sid-query-client-error" },
            [e],
          ),
          region,
          sid,
          gameserverId,
          trigger,
        });
        return;
      }
      if (result.errors) {
        await handleCtxLoadError({
          // Same rationale as above, for GraphQL-level (as opposed to transport-level) errors.
          error: new CtxLoadError("GraphQL server error", { fingerprint: "sid-query-server-error" }, result.errors),
          region,
          sid,
          gameserverId,
          trigger,
        });
        return;
      }

      // reassign sid from result
      sid = result.data.sid || undefined;
      if (!sid) {
        // Server not found by gameserver ID
        await handleCtxLoadError({
          error: new ServiceNotFoundError("Service not found"),
          region,
          sid: sid,
          gameserverId,
          trigger,
        });
        return;
      }
      console.debug("Retrieved service ID:", sid, "from gameserver ID:", gameserverId);
      target.sid = sid;
    }
    // Validate sid as valid i32 to prevent GraphQL from throwing this as an error
    if (sid && !isValidInt32(sid)) {
      await handleCtxLoadError({
        error: new ServiceNotFoundError("Service not found"),
        region,
        sid,
        gameserverId,
        trigger,
      });
      return;
    }
    const rsid = { id: sid, region: region };
    if (inFlightLoad && inFlightLoad.rsid.region == rsid.region && inFlightLoad.rsid.id == rsid.id) {
      // Resolve with that load, not before it: whoever awaits this one still reads fresh data (TR-5544)
      console.debug("Context already loading for RSID:", rsid, ", joining it");
      return inFlightLoad.done;
    }
    let finished: () => void;
    const thisLoad = { rsid, done: new Promise<void>((resolve) => (finished = resolve)) };
    inFlightLoad = thisLoad;
    try {
      const previousRsid = currentRsid.value;
      // Reloads a hidden tab held back for this server are answered by this load as well
      const held = reloadScheduler.started({ id: sid, region: region as string });
      const applied = await loadServiceCtx(rsid, { trigger, merged: [...new Set([...merged, ...held])] });
      if (!applied) return;
      ctxRetry.value.nextAt = 0;
      ctxRetry.value.count = 0;
      // Check if the rsid is already present in the store
      if (previousRsid?.id !== sid || previousRsid?.region !== region) {
        console.debug("Enabling subscriptions for RSID:", rsid);
        await Promise.all([
          subscribeServiceState(rsid),
          subscribeServiceSensors(rsid),
          subscribeServiceConsole(rsid),
          subscribeGameServerQuery(rsid),
          subscribeServiceEvents(rsid),
        ]);
        console.debug("Enabled subscriptions for RSID:", rsid);
      }
    } catch (e) {
      let error = e;
      // If `e` is already a CtxLoadError (thrown by loadServiceCtx below, which
      // sets its own fingerprint per failure point), keep it as-is instead of
      // double-wrapping and losing that more specific grouping key.
      //
      // Anything else still has to be wrapped, but the five subscribeService*
      // calls above throw SubscriptionError from inside this same `try` on a
      // synchronous setup failure. Inheriting their key keeps those five throw
      // sites in five Sentry groups instead of collapsing them into one.
      if (!(error instanceof CtxLoadError)) {
        error = new CtxLoadError(
          "Unable to load service context",
          { cause: e, fingerprint: inheritFingerprint(e, "ctx-load-failed") },
          [e],
        );
      }
      await handleCtxLoadError({ error, region, sid, gameserverId, trigger });
    } finally {
      if (inFlightLoad === thisLoad) inFlightLoad = undefined;
      finished();
      // A stale load leaves the flag to the load of the server the panel is on now
      if (sameLoadTarget(loadTarget, target)) isLoading.value = false;
    }
  }

  /** Loads and applies the ctx of `rsid`. False when the panel has left that server meanwhile: nothing applied */
  async function loadServiceCtx(
    rsid: Rsid_Input,
    { trigger, merged }: { trigger: CtxLoadTrigger; merged: CtxLoadTrigger[] },
  ): Promise<boolean> {
    isLoading.value = true;
    ctxLoadError.value = undefined;
    const hidden = visibility.value === "hidden";
    Sentry.addBreadcrumb({ category: "ctx", message: `ctx load: ${trigger}`, data: { trigger, merged, hidden } });
    const result = await apolloClient.query({
      query: CtxDocument,
      variables: {
        sid: rsid.id,
        region: rsid.region,
      },
      context: { urlParams: ctxLoadUrlParams({ trigger, merged, hidden }) },
    });
    if (result.error) {
      // Distinct fingerprint per failure branch below (ctx-query-error vs.
      // ctx-query-graphql-errors vs. ctx-global-errors further down): they're
      // all "Unable to load service context" for humans, but come from
      // different code paths and should not be merged into one Sentry group.
      throw new CtxLoadError(
        "Unable to load service context",
        { cause: result.error, fingerprint: "ctx-query-error" },
        [result.error as ApolloError],
      );
    } else if (result.errors) {
      // Convert errors (which are simple objects for some reason) to GraphQLError instances
      throw new CtxLoadError(
        "Unable to load service context",
        { fingerprint: "ctx-query-graphql-errors" },
        result.errors.map((err) => new GraphQLError(err.message, null, new Source("", "ctx"), null, err.path)),
      );
    } else if (result.data.cfgContext?.ns) {
      // A reload of the previous server that was still running when the panel switched: applying it would
      // show that server under the new one's URL, whichever of the two loads finishes last
      if (!sameLoadTarget(loadTarget, { region: rsid.region, sid: rsid.id })) {
        console.debug("Discarding the ctx of a server the panel has left:", rsid);
        return false;
      }
      const contextNamespace = result.data.cfgContext.ns;
      // maybe we should automate this based on type reflection?
      const { profile, ...ctxKeep } = contextNamespace;
      if (profile && contextNamespace.sys?.game?.key) {
        profile.publicConfigs = JSON.parse(profile.publicConfigs);
        profile.configDefinition = JSON.parse(profile.configDefinition);
        const newContext = { profile, ...ctxKeep };
        const gameProfileError = ref();
        const gameProfile = (() => {
          try {
            return buildProfile(newContext);
          } catch (e) {
            gameProfileError.value = e;
          }
        })();

        const parsedRoot: ServiceEntry = {
          ...newContext,
          ...(gameProfile ? { gameProfile } : {}),
          changeTracking: {
            publicConfigs: structuredClone(profile.publicConfigs),
            changedFields: {},
          },
        };
        serviceCtx({
          ctxData: parsedRoot,
          ctxErrorsData: result.data.cfgContext.errors,
        });
        if (gameProfileError.value) throw Error(gameProfileError.value);
        client.setFileManagerClient(rsid);
        isLoading.value = false;
      }
      // Check if there are any global load issues reported
      const errors = result.data.cfgContext.errors as Array<CtxErrorParsed>;
      if (errors.length) {
        const globalErrors = errors.filter((err) => err?.scope == FailureScope.Global);
        if (globalErrors.length) {
          // Transform any global errors into a load error
          throw new CtxLoadError(
            "Unable to load service context",
            { fingerprint: "ctx-global-errors" },
            globalErrors.map(
              (err) =>
                new GraphQLError(
                  `Mutator ${err?.mutator} failed: ${err?.error.class_}(${err?.error.args.join(", ")})`,
                  null,
                  null,
                  null,
                  err?.affectedPaths,
                ),
            ),
          );
        }
      }
    } else {
      throw new ServiceNotFoundError("Service not found");
    }
    console.debug("Loaded service context for RSID:", rsid);
    return true;
  }

  const reloadScheduler = createCtxReloadScheduler({
    currentRsid: () => currentRsid.value ?? undefined,
    targetRsid: () =>
      loadTarget?.sid && loadTarget.region ? { id: loadTarget.sid, region: loadTarget.region } : undefined,
    isHidden: () => visibility.value === "hidden",
    load: (rsid, trigger, merged) => {
      // After a failed load the retry timer reloads everything, with backoff. Loading now would bypass it.
      if (ctxRetryTimeout) {
        console.debug(`Skipping ctx reload (${trigger}): a retry is already scheduled`);
        return Promise.resolve();
      }
      return loadAll({ region: rsid.region as Region, sid: rsid.id, trigger, merged });
    },
  });

  /**
   * Reloads the ctx of the current server. Coalesced with any load already running, and held back in a
   * hidden tab for automatic triggers; see util/ctxReloadScheduler.ts.
   */
  function reloadServiceCtx(trigger: CtxLoadTrigger): Promise<void> {
    return reloadScheduler.request(trigger);
  }

  // Run the reloads a hidden tab held back, once it is shown again
  watch(visibility, (current) => {
    if (current === "visible") reloadScheduler.shown();
  });

  // Reload the ctx once the service's FSM transition has finished. Not when the transition "ended" with a
  // ctx load, i.e. the panel switched away from a server that was transitioning: that ctx is fresh already.
  watch([serviceIsTransitioning, ctxLoadCount], async ([isTransitioning, loads], [wasTransitioning, loadsBefore]) => {
    if (!isTransitioning && wasTransitioning && loads === loadsBefore) {
      console.debug("State stopped transitioning, trying to reload service context");
      await reloadServiceCtx("transition_end");
    }
  });

  // Maintain dynamic section alias: keep publicConfigs[alias] pointing to the
  // config object for the currently selected item (e.g. "world" → "world_<id>").
  // Because it's a reference assignment, mutations via buildProperty("world", ...)
  // flow through to the underlying "world_<id>" entry automatically.
  watchEffect(() => {
    const profile = ctx.value.profile;
    const ds = ctx.value.gameProfile?.dynamicSection;
    if (!profile || !ds) return;
    // Configs failed to load (e.g. ParseConfigException) — don't fabricate the alias key,
    // it would make publicConfigs look available and hide the ctx error view.
    if (Object.keys(profile.publicConfigs).length === 0) return;

    const { controllerConfig, controllerField, targetConfigPrefix, targetConfigAlias } = ds;
    const controllerValue = profile.publicConfigs[controllerConfig]?.[controllerField] as string | undefined;

    if (controllerValue) {
      const sourceConfig = profile.publicConfigs[targetConfigPrefix + controllerValue];
      profile.publicConfigs[targetConfigAlias] = sourceConfig ?? {};
    } else {
      profile.publicConfigs[targetConfigAlias] = {};
    }
  });

  // If the Window loses focus, stop any current load retries
  watchEffect(() => {
    if (!focused.value && ctxRetryTimeout) {
      console.debug("Page lost focus, clearing current retry attempt");
      lostFocus.value = true;
      clearCtxRetry();
    }
  });

  /** Cancels the retry timer of a failed load, if one is waiting */
  function clearCtxRetry() {
    clearTimeout(ctxRetryTimeout);
    ctxRetryTimeout = undefined;
  }

  async function handleCtxLoadError({
    error,
    region,
    sid,
    gameserverId,
    trigger,
  }: {
    error: CtxLoadError;
    region?: Region;
    sid?: number;
    gameserverId?: number;
    trigger: CtxLoadTrigger;
  }) {
    const captureContext = { tags: { ctx_trigger: trigger } };
    Sentry.setExtra("region", region);
    Sentry.setExtra("sid", sid);
    Sentry.setExtra("gameserverId", gameserverId);
    if ((error?.errors ?? []).length) {
      // One nested GraphQLError per entry in `error.errors` gets its own
      // CtxLoadError + Sentry capture (so each has its own errorId for the UI),
      // but they all propagate the *original* error's fingerprint via the
      // `fingerprint` override — otherwise this re-wrap would default to
      // grouping by "Unable to load context" and collapse every throw site
      // in loadServiceCtx back into one bucket, undoing the whole point of
      // giving each of them a distinct fingerprint above.
      for (const e of error.errors) {
        error.errorIds.push(
          Sentry.captureException(
            new CtxLoadError("Unable to load context", {
              cause: e,
              detail: e.message,
              fingerprint: error.sentryFingerprint,
            }),
            captureContext,
          ),
        );
      }
    } else {
      error.errorIds.push(
        Sentry.captureException(
          new CtxLoadError("Unable to load context", {
            cause: error,
            detail: error.message,
            fingerprint: error.sentryFingerprint,
          }),
          captureContext,
        ),
      );
    }

    // A load of a server the panel has left: reported above, but neither shown nor retried
    if (!sameLoadTarget(loadTarget, { region, sid, gameserverId })) return;
    ctxLoadError.value = error;
    isLoading.value = false;
    if (!(error instanceof ServiceNotFoundError) && region) {
      const attempt = ctxRetry.value?.count ?? 0;
      const delay = ref<number>(0);
      if (attempt > 1) {
        const cap = CTX_RETRY_MAX_DELAY - CTX_RETRY_LONG_DELAY_HEAD_START;
        const minimum = Math.min(cap, CTX_RETRY_BASE_DELAY * 2 ** attempt);
        delay.value = CTX_RETRY_LONG_DELAY_HEAD_START + Math.floor(Math.random() * (cap - minimum + 1)) + minimum;
      } else {
        delay.value =
          Math.floor(Math.random() * (CTX_RETRY_SHORT_DELAY_MAX - CTX_RETRY_SHORT_DELAY_MIN + 1)) +
          CTX_RETRY_SHORT_DELAY_MIN;
      }
      const nextAt = Date.now() + delay.value;
      if (!ctxRetry.value) {
        ctxRetry.value = { nextAt, count: 1 };
      } else {
        ctxRetry.value.nextAt = nextAt;
        ctxRetry.value.count += 1;
      }

      // Handle retry. One timer at most: a second failure replaces the first's timer instead of adding one.
      clearCtxRetry();
      ctxRetryTimeout = setTimeout(() => {
        ctxRetryTimeout = undefined;
        loadAll({ region, sid, gameserverId, trigger: "retry" });
      }, delay.value);
    }
  }

  async function subscribeServiceState(rsid: Rsid_Input) {
    try {
      if (subscriptions.state) subscriptions.state.unsubscribe();
      const observable = apolloClient.subscribe({
        query: ServiceStateDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
      subscriptions.state = observable.subscribe({
        next: (data) => {
          if (data.data) {
            serviceState(data.data.serviceState);
          }
        },
        // Below and in every other subscribeService* function in this file: the
        // first argument is a fixed, per-throw-site `context` string (no
        // interpolation) so it doubles as the default Sentry fingerprint; the
        // upstream error's own message — which for gRPC/Apollo errors can embed
        // timestamps or other variable data — goes in `detail` instead, where
        // it's visible in the rendered message but excluded from grouping.
        error(errorValue: Error) {
          throw new SubscriptionError("Error from service state subscription", {
            cause: errorValue,
            detail: errorValue.message,
          });
        },
        // complete => {}
      });
    } catch (e) {
      throw new SubscriptionError("Unable to subscribe to service state", { cause: e, detail: errorMessage(e) });
    }
  }

  async function subscribeServiceSensors(rsid: Rsid_Input) {
    try {
      if (subscriptions.sensors) subscriptions.sensors.unsubscribe();
      const observable = apolloClient.subscribe({
        query: ServiceSensorsDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
      subscriptions.sensors = observable.subscribe({
        next: (data) => {
          sensors.value = data.data?.serviceSensors;
        },
        error(errorValue: Error) {
          throw new SubscriptionError("Error from service sensors subscription", {
            cause: errorValue,
            detail: errorValue.message,
          });
        },
        // complete => {}
      });
    } catch (e) {
      throw new SubscriptionError("Unable to subscribe to service sensors", { cause: e, detail: errorMessage(e) });
    }
  }

  async function ensureServiceConsoleSubscribed(rsid: Rsid_Input) {
    if (!subscriptions.console || subscriptions.console.closed) {
      await subscribeServiceConsole(rsid);
    }
  }

  async function subscribeServiceConsole(rsid: Rsid_Input) {
    try {
      // throw new Error("subscription error test");
      // Start with fresh backoff when subscribing to a different service, but
      // keep the accumulated count for the same one so backoff stays effective.
      if (consoleRetry.rsid && (consoleRetry.rsid.id !== rsid.id || consoleRetry.rsid.region !== rsid.region)) {
        consoleRetry.count = 0;
      }
      consoleRetry.rsid = rsid;
      if (consoleRetry.timeout) {
        clearTimeout(consoleRetry.timeout);
        consoleRetry.timeout = undefined;
      }
      if (subscriptions.console) subscriptions.console.unsubscribe();
      const observable = apolloClient.subscribe({
        query: ConsoleMessagesDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
      subscriptions.console = observable.subscribe({
        next: (data) => {
          if (data.data) {
            consoleRetry.count = 0;
            const messages = ctx.value.consoleMessages;
            messages.push(data.data.consoleMessages);
            if (messages.length > CONSOLE_MESSAGE_LIMIT) {
              messages.splice(0, messages.length - CONSOLE_MESSAGE_LIMIT);
            }
          }
        },
        error(errorValue: Error) {
          // This is the throw site that originally triggered TV-1870: `errorValue`
          // here is the raw gRPC/Apollo error whose message embeds an
          // `AioRpcError` repr with a `created_time` timestamp (added by TR-5166).
          // Fixed `message` as the fingerprinted context + that variable text as
          // `detail` is exactly what keeps this a single stable Sentry group again.
          const message = "Error from console messages subscription";
          const wrapper = new SubscriptionError(message, { cause: errorValue, detail: errorValue.message });
          console.warn(wrapper.message);
          if (CONSOLE_PERMANENT_ERRORS.some((e) => errorValue.message.includes(e))) {
            Sentry.withScope(function (scope) {
              scope.setLevel("info");
              Sentry.captureException(wrapper);
            });
            return;
          }
          // Anything else is treated as transient (daemon restart, game process
          // stopped/restarting, dropped stream): report the first failure of a
          // streak, then keep re-subscribing with backoff.
          if (consoleRetry.count === 0) {
            Sentry.withScope(function (scope) {
              scope.setLevel("info");
              Sentry.captureException(wrapper);
            });
          }
          scheduleConsoleResubscribe(rsid);
        },
        complete: () => {
          // The daemon ends the stream e.g. when the game process restarts;
          // re-subscribe so the console keeps following the new process.
          scheduleConsoleResubscribe(rsid);
        },
      });
    } catch (e) {
      throw new SubscriptionError("Unable to subscribe to console messages", { cause: e, detail: errorMessage(e) });
    }
  }

  function scheduleConsoleResubscribe(rsid: Rsid_Input) {
    if (consoleRetry.timeout) return;
    // Jittered so that the clients of a daemon that restarted do not all come back in the same second.
    const delay = Math.min(
      CONSOLE_RETRY_MAX_DELAY,
      CONSOLE_RETRY_BASE_DELAY * 2 ** consoleRetry.count * (0.5 + Math.random()),
    );
    consoleRetry.count += 1;
    console.debug(`Re-subscribing console messages in ${delay}ms (attempt ${consoleRetry.count})`);
    consoleRetry.timeout = setTimeout(() => {
      consoleRetry.timeout = undefined;
      const active = currentRsid.value;
      // The user may have navigated to a different service in the meantime
      if (!active || active.id !== rsid.id || active.region !== rsid.region) return;
      subscribeServiceConsole(rsid).catch((e) => {
        // A synchronous setup failure never reaches the observer's error
        // callback, so it has to keep the retry loop alive itself.
        console.warn("Console re-subscribe failed:", e);
        if (!CONSOLE_PERMANENT_ERRORS.some((message) => errorMessage(e).includes(message))) {
          scheduleConsoleResubscribe(rsid);
        }
      });
    }, delay);
  }

  async function subscribeServiceEvents(rsid: Rsid_Input) {
    try {
      if (subscriptions.events) subscriptions.events.unsubscribe();
      const observable = apolloClient.subscribe({
        query: ServiceEventsDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
      subscriptions.events = observable.subscribe({
        next: (data) => {
          const event = data.data?.serviceEvents;
          if (event?.event === "backup_created" && event?.payload?.type === "user") {
            // User-initiated backup completed
            createToast({
              title: t("ngp/backup/action/create/notification/success/title"),
              text: t("ngp/backup/action/create/notification/success/text"),
            });
          }
          // The subscription only carries backup_created (BackupCreatedEvent)
          reloadServiceCtx(backupEventTrigger(event?.payload?.type));
        },
        error(errorValue: Error) {
          throw new SubscriptionError("Error from service events subscription", {
            cause: errorValue,
            detail: errorValue.message,
          });
        },
        // complete => {}
      });
    } catch (e) {
      throw new SubscriptionError("Unable to subscribe to service events", { cause: e, detail: errorMessage(e) });
    }
  }

  async function subscribeGameServerQuery(rsid: Rsid_Input) {
    try {
      if (subscriptions.gameServer) subscriptions.gameServer.unsubscribe();
      const observable = apolloClient.subscribe({
        query: GameServerQueryDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
      subscriptions.gameServer = observable.subscribe({
        next: (data) => {
          if (data.data) {
            gameServerState.value = data.data.gameServerQuery;
          }
        },
        error(errorValue: Error) {
          throw new SubscriptionError("Error from gameserver query subscription", {
            cause: errorValue,
            detail: errorValue.message,
          });
        },
        // complete => {}
      });
    } catch (e) {
      throw new SubscriptionError("Unable to subscribe to gameserver query", { cause: e, detail: errorMessage(e) });
    }
  }

  async function stopService(force_stop: boolean = false, rsid: Rsid_Input) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    // Set the state here for optimistic UI, but keep the previous state, so we can restore it in case of failure
    const prevTargetState = ctx.value.service?.config?.state;
    serviceTargetState(Service_States.Stopped);
    try {
      await apolloClient.mutate({
        mutation: StopServiceDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          force: force_stop,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
      // Reset target state back to what it was before
      if (prevTargetState) serviceTargetState(prevTargetState);
    }
  }

  /**
   * Restart the server. With "wait until empty" switched on (TR-5485) the daemon may hold the restart while players
   * are online; `force` (the waiting banner's "Restart now") skips that.
   */
  async function restartService(rsid: Rsid_Input, force: boolean = false) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    const prevTargetState = ctx.value.service?.config?.state;
    serviceTargetState(Service_States.Running);
    commitInProgress.value = true;
    try {
      await apolloClient.mutate({
        mutation: RestartServiceDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          force,
        },
        context: {
          timeout: (ctx.value.service?.config?.startTimeout ?? 10) * 1000 + 10000,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
      // Reset target state back to what it was before
      if (prevTargetState) serviceTargetState(prevTargetState);
    } finally {
      commitInProgress.value = false;
    }
    // TODO: lock ui and move everything into transient state, maybe by going into a virtual restarting state
  }

  async function reinstallService(wipe: boolean = false, rsid: Rsid_Input) {
    const notificationKey = wipe
      ? "ngp/status/action/reset/notification/started"
      : "ngp/status/action/repair/notification/started";
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    createToast({
      title: t(notificationKey + "/title"),
      text: t(notificationKey + "/text"),
    });
    try {
      await apolloClient.mutate({
        mutation: ReinstallServiceDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          wipe: wipe,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function deleteSaveGames() {
    const rsid = currentRsid.value;
    if (!rsid) return;
    try {
      await apolloClient.mutate({
        mutation: DeleteSaveGamesDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
    createToast({
      title: t("ngp/status/action/deletesavegames/notification/started/title"),
      text: t("ngp/status/action/deletesavegames/notification/started/text"),
    });
  }

  async function skipBroadcast(rsid: Rsid_Input) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      await apolloClient.mutate({
        mutation: SkipBroadcastDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function commitService() {
    const rsid = currentRsid.value;
    if (!rsid) return;
    const blockedKey = ctx.value.gameProfile?.commitBlockedMessageKey?.();
    if (blockedKey) {
      createToast({
        title: t(blockedKey),
        toastType: "danger",
      });
      return;
    }
    commitInProgress.value = true;
    try {
      await apolloClient.mutate({
        mutation: ServiceCtxDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
          configs: JSON.stringify(ctx.value?.profile?.publicConfigs),
        },
        context: {
          timeout: (ctx.value.service?.config?.startTimeout ?? 10) * 1000 + 10000,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    } finally {
      commitInProgress.value = false;
    }
    await reloadServiceCtx("config_save");
  }

  async function createPermission({ rsid, userName }: { rsid: Rsid; userName: string }) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    const result = await apolloClient.mutate({
      mutation: CreatePermissionDocument,
      variables: {
        sid: serviceId.id,
        region: serviceId.region,
        userName,
      },
    });
    ctx.value.sys?.permissions?.push(result.data?.createPermission?.permission);
  }

  async function deletePermission({ rsid, userName }: { rsid?: Rsid; userName: string }) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      await apolloClient.mutate({
        mutation: DeletePermissionDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          userName,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
    if (ctx.value.sys?.permissions) {
      for (const [index, permission] of ctx.value.sys.permissions.entries()) {
        if (permission?.userName === userName) {
          ctx.value.sys.permissions.splice(index, 1);
          break;
        }
      }
    }
  }

  async function setCustomHostname({ hostname, tld }: { hostname: Maybe<string>; tld: Maybe<string> }) {
    const rsid = currentRsid.value;
    if (!rsid) return;
    try {
      const response = await apolloClient.mutate({
        mutation: SetCustomHostnameDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
          hostname,
          tld,
        },
      });
      if (response.data?.setCustomHostname) {
        if (ctx.value.service?.config) {
          ctx.value.service.config.dnsNames = response.data.setCustomHostname.dnsNames;
        }
      }
    } catch (e) {
      handleApolloErrorDefault(e);
      return;
    }
    return true;
  }

  async function createRestartTask({ rsid, restartTask }: { rsid: Rsid; restartTask: RestartTaskInput }) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      const result = await apolloClient.mutate({
        mutation: CreateRestartTaskDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          restartTask: restartTask,
        },
      });
      if (result.data?.createRestartTask) {
        ctx.value.service?.restartSchedule?.push(result.data.createRestartTask.restartTask);
        createToast({
          title: t("ngp/restarts/action/add/notification/success/title"),
          text: t("ngp/restarts/action/add/notification/success/text"),
        });
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function deleteRestartTask({ rsid, restartTaskId }: { rsid: Rsid; restartTaskId: number }) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      await apolloClient.mutate({
        mutation: DeleteRestartTaskDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          restartTaskId,
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
    if (ctx.value.service?.restartSchedule) {
      for (const [index, restartTask] of ctx.value.service.restartSchedule.entries()) {
        if (restartTask?.id === restartTaskId) {
          ctx.value.service.restartSchedule.splice(index, 1);
          break;
        }
      }
    }
    createToast({
      title: t("ngp/restarts/action/delete/notification/success/title"),
      text: t("ngp/restarts/action/delete/notification/success/text"),
    });
  }

  async function resetFtpPassword(rsid: Rsid) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      const result = await apolloClient.mutate({
        mutation: ResetFtpPasswordDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
        },
      });
      if (ctx.value.service?.config) {
        ctx.value.service.config.ftpPassword = result.data?.resetFtpPassword?.config?.ftpPassword;
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  function updateMysqlDb(mysqlDb: CustomerMysqlDb) {
    if (ctx.value.sys) {
      ctx.value.sys.mysqlDb = mysqlDb;
    }
  }

  async function resetMysqlDbPassword(rsid: Rsid) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      const result = await apolloClient.mutate({
        mutation: ResetMysqlDbPasswordDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
        },
      });
      updateMysqlDb(result.data?.resetMysqlDbPassword?.mysqlDb);
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function createMysqlDb(rsid: Rsid) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return false;
    try {
      const result = await apolloClient.mutate({
        mutation: CreateMysqlDbDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
        },
      });
      updateMysqlDb(result.data?.resetMysqlDbPassword?.mysqlDb);
      return true;
    } catch (e) {
      handleApolloErrorDefault(e);
      return false;
    }
  }

  async function runCommand({
    rsid,
    command,
    kwargs,
  }: {
    rsid: Rsid;
    command: string;
    kwargs: Record<string, unknown>;
  }) {
    const serviceId = rsid ?? currentRsid.value;
    if (!serviceId) return;
    try {
      await apolloClient.mutate({
        mutation: RunCommandDocument,
        variables: {
          sid: serviceId.id,
          region: serviceId.region,
          command: command,
          kwargs: JSON.stringify(kwargs),
        },
      });
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function setAutoUpdate(autoupdate: boolean) {
    const rsid = currentRsid.value;
    if (!rsid) return;
    try {
      await apolloClient.mutate({
        mutation: SetAutoUpdateDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
          autoupdate: autoupdate,
        },
      });
      await reloadServiceCtx("auto_update_toggle");
    } catch (e) {
      console.error("Something went wrong: " + errorMessage(e));
    }
  }

  /** Store how long a restart may wait for players to leave (TR-5485); minutes, or null for off. */
  async function setWaitUntilEmpty(minutes: number | null) {
    const rsid = currentRsid.value;
    if (!rsid) return;
    try {
      const { data } = await apolloClient.mutate({
        mutation: SetWaitUntilEmptyDocument,
        variables: {
          sid: rsid.id,
          region: rsid.region,
          minutes,
        },
      });
      const info = data?.setWaitUntilEmpty?.waitUntilEmpty;
      if (info && ctx.value.profile) {
        (ctx.value.profile as { waitUntilEmpty?: WaitUntilEmptyFieldsFragment | null }).waitUntilEmpty = info;
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function forceUpdate() {
    const rsid = currentRsid.value;
    if (!rsid) return;
    const response = await apolloClient.mutate({
      mutation: ForceUpdateDocument,
      variables: { sid: rsid.id, region: rsid.region },
      context: { autoError: true },
    });
    return response.data?.forceUpdate?.ok;
  }

  async function restoreFiles(paths: Array<string>) {
    await runCommand({ rsid: undefined, command: "restore_files", kwargs: { paths } });
    await reloadServiceCtx("restore_files");
  }

  return {
    isLoading,
    gameServerState,
    loadAll,
    ensureServiceConsoleSubscribed,
    ctx,
    stopService,
    restartService,
    skipBroadcast,
    ctxLoadError,
    ctxRetry,
    reloadServiceCtx,
    createRestartTask,
    reinstallService,
    deleteSaveGames,
    resetFtpPassword,
    resetMysqlDbPassword,
    createMysqlDb,
    setCustomHostname,
    restoreFiles,
    ctxErrors,
    expertModeActive,
    commitInProgress,
    deletePermission,
    createPermission,
    setConfField,
    setConfig,
    deleteRestartTask,
    discardConfChanges,
    commitService,
    runCommand,
    serviceStates,
    serviceIsTransitioning,
    showWarning,
    setWarningMsg,
    interactivityLocked,
    sensors,
    consoleMessages,
    lostFocus,
    setAutoUpdate,
    setWaitUntilEmpty,
    waitUntilEmpty,
    forceUpdate,
  };
});
